import React, { useEffect, useState, useMemo } from 'react';

const fetchProducts = async () => {
  const res = await fetch('/api/printful-products', { headers: { 'Accept': 'application/json' } });
  if (!res.ok) {
    // Try to parse error body for diagnostics
    const text = await res.text();
    throw new Error(text?.startsWith('<') ? 'Serverless API not available locally. Start functions (e.g., vercel dev) or deploy.' : (text || 'Failed to load products'));
  }
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await res.text();
    throw new Error(text?.startsWith('<') ? 'Serverless API returned HTML (likely dev server index). Run with vercel dev or deploy.' : 'Unexpected response format');
  }
  return res.json();
};

const createCheckout = async (items) => {
  const res = await fetch('/api/create-checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items,
      success_url: `${window.location.origin}/merch?status=success`,
      cancel_url: `${window.location.origin}/merch?status=cancel`
    })
  });
  if (!res.ok) throw new Error('Failed to create checkout');
  return res.json();
};

const Merch1 = () => {
  const [data, setData] = useState({ products: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const json = await fetchProducts();
        if (isMounted) setData(json);
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const addToCart = (product, variant) => {
    setCart((prev) => {
      const key = `${variant.sync_variant_id}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [
        ...prev,
        {
          key,
          name: product.name + ' - ' + variant.name,
          printful_sync_variant_id: variant.sync_variant_id,
          unit_amount: parseFloat(variant.retail_price || '0'),
          currency: (variant.currency || 'USD').toLowerCase(),
          quantity: 1,
          images: variant.image ? [variant.image] : (product.thumbnail ? [product.thumbnail] : undefined),
        }
      ];
    });
  };

  const incrementQty = (key) => setCart(prev => prev.map(i => i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
  const decrementQty = (key) => setCart(prev => prev.flatMap(i => {
    if (i.key !== key) return i;
    const q = Math.max(0, (i.quantity || 1) - 1);
    return q === 0 ? [] : { ...i, quantity: q };
  }));
  const removeItem = (key) => setCart(prev => prev.filter(i => i.key !== key));

  const total = useMemo(() => cart.reduce((acc, i) => acc + i.unit_amount * i.quantity, 0), [cart]);

  const checkout = async () => {
    try {
      const { url } = await createCheckout(cart);
      if (url) window.location.href = url;
    } catch (e) {
      alert(e.message || 'Checkout failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-extrabold mb-6" style={{
          color: '#111827',
          textShadow: '-1px -1px 0 #ffffff, 1px -1px 0 #ffffff, -1px 1px 0 #ffffff, 1px 1px 0 #ffffff'
        }}>Merch</h1>
        {loading && <div className="p-6 bg-gray-50 border border-gray-200 rounded-xl">Loading products…</div>}
        {error && (
          <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {String(error)}
            <div className="text-xs text-red-600 mt-2">Tip: In local CRA, /api routes aren’t served. Use "vercel dev" or deploy to run serverless APIs.</div>
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.products.map((p) => (
              <div key={p.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col">
                <div className="aspect-square bg-gray-50 flex items-center justify-center">
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt={p.name} className="object-contain max-h-full" />
                  ) : (
                    <div className="text-gray-400">No image</div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-semibold text-gray-900 mb-2">{p.name}</h3>
                  <div className="space-y-2 mb-4 flex-1">
                    {(p.variants || []).filter(v => !v.is_disabled).slice(0, 5).map((v) => (
                      <div key={v.sync_variant_id} className="flex items-center justify-between gap-2">
                        <div className="text-sm text-gray-700 truncate">{v.name}</div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium">{v.currency} {v.retail_price}</div>
                          <button
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            onClick={() => addToCart(p, v)}
                          >Add</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Cart */}
        <div className="mt-10 p-4 bg-white border border-gray-200 rounded-xl">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Cart</h2>
          {cart.length === 0 ? (
            <div className="text-sm text-gray-600">Your cart is empty.</div>
          ) : (
            <div>
              <ul className="space-y-3">
                {cart.map((item) => (
                  <li key={item.key} className="border border-gray-100 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-16 h-16 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.name} className="object-contain w-full h-full" />
                      ) : (
                        <div className="text-gray-400 text-xs">No image</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.currency.toUpperCase()} {item.unit_amount.toFixed(2)} each</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => decrementQty(item.key)} className="w-7 h-7 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">−</button>
                      <div className="w-8 text-center text-sm">{item.quantity}</div>
                      <button onClick={() => incrementQty(item.key)} className="w-7 h-7 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">+</button>
                    </div>
                    <div className="w-24 text-right text-sm font-semibold">
                      {item.currency.toUpperCase()} {(item.unit_amount * item.quantity).toFixed(2)}
                    </div>
                    <button onClick={() => removeItem(item.key)} className="ml-2 text-xs text-red-600 hover:text-red-700">Remove</button>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">Total</div>
                <div className="text-lg font-semibold">USD {total.toFixed(2)}</div>
              </div>
              <div className="mt-4 flex items-center justify-end">
                <button
                  onClick={checkout}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >Checkout</button>
              </div>
              <p className="mt-2 text-xs text-gray-500">Shipping info is placeholder; add address capture to fulfill automatically.</p>
            </div>
          )}
        </div>
    </div>
  );
};

export default Merch1;


